/**
 * Filter panel component.
 *
 * Provides filtering controls for partner search including level, goal,
 * location, meeting preference, and availability filters. Handles filter
 * state management and communicates changes to parent component.
 *
 * @module FilterPanel
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { PartnerSearchFilters, GermanLevel, DayOfWeek } from '@/types/partner-finder';
import { learningGoals, locations, DAYS } from '@/data/partnerFinderData';
import { translatePartnerDay, translatePartnerGoal, translatePartnerLocation } from '@/lib/partnerFinderLabels';
import { useTranslation } from 'react-i18next';

const LEVELS: GermanLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

interface FilterPanelProps {
  readonly filters: PartnerSearchFilters;
  readonly isMobile: boolean;
  readonly onApply: (filters: PartnerSearchFilters) => void;
}

/**
 * Component for partner search filters panel
 */
export function FilterPanel({ filters, isMobile, onApply }: FilterPanelProps) {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<PartnerSearchFilters>(filters);

  // Sync local filters when parent filters change (e.g., when cleared)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (newFilters: PartnerSearchFilters) => {
    setLocalFilters(newFilters);
    // On desktop, apply filters immediately
    if (!isMobile) {
      onApply(newFilters);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Keyword */}
      <div className="space-y-2">
        <Label htmlFor="keyword">{t('partnerFinder.search.searchLabel', { defaultValue: 'Search' })}</Label>
        <Input
          id="keyword"
          placeholder={t('partnerFinder.search.nameOrDescriptionPlaceholder', {
            defaultValue: 'Name or description',
          })}
          value={localFilters.keyword || ''}
          onChange={(e) => {
            const value = e.target.value.trim() || undefined;
            handleFilterChange({ ...localFilters, keyword: value });
          }}
        />
      </div>

      {/* Level */}
      <div className="space-y-2">
        <Label>{t('partnerFinder.search.germanLevelLabel', { defaultValue: 'German Level' })}</Label>
        <Select
          value={localFilters.level || 'all'}
          onValueChange={(value) => {
            const newFilters = { ...localFilters };
            if (value === 'all') {
              delete newFilters.level;
            } else {
              newFilters.level = value as GermanLevel;
            }
            handleFilterChange(newFilters);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('partnerFinder.search.anyLevel', { defaultValue: 'Any level' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('partnerFinder.search.anyLevel', { defaultValue: 'Any level' })}</SelectItem>
            {LEVELS.map((level) => (
              <SelectItem key={level} value={level}>
                {level}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Goal */}
      <div className="space-y-2">
        <Label>{t('partnerFinder.search.learningGoalLabel', { defaultValue: 'Learning Goal' })}</Label>
        <Select
          value={localFilters.goal || 'all'}
          onValueChange={(value) => {
            const newFilters = { ...localFilters };
            if (value === 'all') {
              delete newFilters.goal;
            } else {
              newFilters.goal = value;
            }
            handleFilterChange(newFilters);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('partnerFinder.search.anyGoal', { defaultValue: 'Any goal' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('partnerFinder.search.anyGoal', { defaultValue: 'Any goal' })}</SelectItem>
            {learningGoals.map((goal) => (
              <SelectItem key={goal.id} value={goal.label}>
                {translatePartnerGoal(t, goal.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Meeting Preference */}
      <div className="space-y-2">
        <Label>{t('partnerFinder.search.meetingPreferenceLabel', { defaultValue: 'Meeting Preference' })}</Label>
        <Select
          value={localFilters.meetingPreference || 'all'}
          onValueChange={(value) => {
            const newFilters = { ...localFilters };
            if (value === 'all') {
              delete newFilters.meetingPreference;
            } else {
              newFilters.meetingPreference = value as 'online' | 'in-person';
            }
            handleFilterChange(newFilters);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('partnerFinder.search.anyMeetingPreference', { defaultValue: 'Any' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('partnerFinder.search.anyMeetingPreference', { defaultValue: 'Any' })}</SelectItem>
            <SelectItem value="online">{t('partnerFinder.values.meeting.online', { defaultValue: 'Online' })}</SelectItem>
            <SelectItem value="in-person">{t('partnerFinder.values.meeting.in-person', { defaultValue: 'In-person' })}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Location (only if in-person or not selected) */}
      {(localFilters.meetingPreference === 'in-person' || !localFilters.meetingPreference) && (
        <div className="space-y-2">
          <Label>{t('partnerFinder.search.locationLabel', { defaultValue: 'Location' })}</Label>
          <Select
            value={localFilters.location || 'all'}
            onValueChange={(value) => {
              const newFilters = { ...localFilters };
              if (value === 'all') {
                delete newFilters.location;
              } else {
                newFilters.location = value;
              }
              handleFilterChange(newFilters);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('partnerFinder.search.anyLocation', { defaultValue: 'Any location' })} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('partnerFinder.search.anyLocation', { defaultValue: 'Any location' })}</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.label}>
                  {translatePartnerLocation(t, loc.label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Availability */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">
          {t('partnerFinder.search.availabilityLabel', { defaultValue: 'Availability' })}
        </Label>
        <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
          {DAYS.map((day) => (
            <div
              key={day}
              className="flex items-center space-x-3 p-2 rounded-md hover:bg-accent/50 transition-colors border border-transparent hover:border-border cursor-pointer group"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  const checkbox = document.getElementById(day) as HTMLInputElement;
                  if (checkbox) checkbox.click();
                }
              }}
              onClick={() => {
                const checkbox = document.getElementById(day) as HTMLInputElement;
                if (checkbox) checkbox.click();
              }}
            >
              <Checkbox
                id={day}
                checked={localFilters.availability?.includes(day) || false}
                onCheckedChange={(checked) => {
                  const currentDays = localFilters.availability || [];
                  let newAvailability: DayOfWeek[] | undefined;

                  if (checked) {
                    newAvailability = [...currentDays, day];
                  } else {
                    const filtered = currentDays.filter((d) => d !== day);
                    newAvailability = filtered.length > 0 ? filtered : undefined;
                  }

                  const newFilters = { ...localFilters };
                  if (newAvailability) {
                    newFilters.availability = newAvailability;
                  } else {
                    delete newFilters.availability;
                  }
                  handleFilterChange(newFilters);
                }}
                className="pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              />
              <Label htmlFor={day} className="text-sm font-normal cursor-pointer flex-1 py-1">
                {translatePartnerDay(t, day)}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Apply Button - Only show on mobile */}
      {isMobile && (
        <Button onClick={() => onApply(localFilters)} className="w-full">
          {t('partnerFinder.buttons.applyFilters', { defaultValue: 'Apply Filters' })}
        </Button>
      )}
    </div>
  );
}
